<template>
  <v-row>
     <v-col cols="12">
        <div class="py-4 datamapper__header">
          <div class="h2">Data Mapper</div>
          <div
            class="datamapper__close"
            @click="closeDataMapper"
          >
            <apio-icon size="24" name="cross" />
          </div>
        </div>
    </v-col>
    <v-col cols="3">
      <label for="payload">Payload</label>
      <v-textarea @input="parser" outlined v-model="node" />
    </v-col>
    <v-col :cols="singleLine ? 9 : 6">
      <label for="mapper"
        >Mapper {{ singleLine ? selectedDatapath : "" }}</label
      >
      <div class="mt-4 mb-2" v-if="searchMatches.length > 0">
        Search results:
      </div>
      <div
        v-if="searchMatches.length < 100"
        :class="{ 'search-results': searchMatches.length > 0 }"
        class="d-flex flex-wrap mb-2"
      >
        <v-chip
          class="ma-1 pa-1"
          small
          color="green white--text"
          @click="scrollTo(match.id)"
          :key="match.id"
          v-for="(match, i) in searchMatches"
        >
          {{ match.readableLabel }} #{{ i }}
        </v-chip>
      </div>
      <div v-else class="d-flex flex-wrap mb-2">
        <v-chip class="ma-1 pa-1" small color="red white--text">
          There are over 100 matching results. Please refine your search.
        </v-chip>
      </div>
      <v-text-field
        @input="findLabels"
        v-if="copyOfNode && !payloadError"
        v-model="searchString"
        dense
        outlined
        placeholder="Search mapper"
      />
      <MItem
        v-if="copyOfNode"
        id="mapper"
        class="datamapper"
        :class="{
          'datamapper-small':
            searchMatches.length > 0 && searchMatches.length < 100,
          'datamapper-error': searchMatches.length > 100,
        }"
        :sel="sel"
        rprefix
        prefix
        :part.sync="copyOfNode"
      />
      <v-alert v-if="payloadError" class="mt-4" dense type="warning"
        >Please check the payload for formatting errors</v-alert
      >
      <v-alert v-if="showEmptyPayloadAlert" class="mt-4" dense type="info"
        >No payload provided</v-alert
      >
    </v-col>
    <v-col cols="12" md="3" v-if="!singleLine">
      <label for="labels">Selected Labels</label>

      <div v-if="dataMapperLabels" class="d-flex flex-wrap mb-2">
        <v-chip
          v-for="(label, i) in dataMapperLabels.paths"
          :key="i"
          class="ma-1 pa-4"
          :class="{ 'selected': isSelected(label.label) }"
          small
          color="green white--text"
        >
          {{ label.label }}
          <apio-icon v-if="!isSelected(label.label)" lightModeColour="white" darkModeColour="white" size="16" name="ui-magnify-close" class="ml-2" @click.native="findAllLabelMatches(label)"/>

          <apio-icon name="ui-magnify-close" lightModeColour="white" darkModeColour="white" v-if="isSelected(label.label)" class="ml-2" size="16" @click="findAllLabelMatches(label)"/>
          <apio-icon size="16" name="cross" lightModeColour="white" darkModeColour="white"  class="ml-2" @click.native="removeLabel(label.label)"/>
        </v-chip>
      </div>

      <div class="mt-4">
        <label for="label">Label</label>
        <v-text-field dense outlined v-model="label" id="label" />
      </div>
      <v-btn color="primary" :ripple="false" @click="add">Add</v-btn>
    </v-col>
    <apio-snackbar :text="snackBarText" :status="snackBarStatus" :snackbar="snackbar" @close="snackbar = false" />
  </v-row>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import request from '@/API/';
import debounce from "lodash.debounce";

export default {
  name: "DataMapper",
  components: {
    MItem: () => import("./MItem.vue"),
  },
  props: {
    singleLine: {
      type: Boolean,
      default: false,
    },
    viewSource: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      all: {},
      sel: {
        payload: {},
        selected: "",
        selection: [""],
      },
      node: {},
      copyOfNode: null,
      data: null,
      path: "",
      selection: [],
      selected: "",
      filecontent: '[{"sku1":1},{"sku2":4}]',
      label: "",
      currentlabel: "",
      payloadError: false,
      searchString: "",
      searchMatches: [],
      selectedLabels: new Set(),
      snackbar: false,
      snackbarText: "",
      snackBarStatus: null,
    };
  },
  computed: {
    ...mapGetters(["dataMapperLabels"]),
    ...mapState({
      qitem: (state) => state.datamapper.qitem,
      selectedDatapath: (state) => state.datamapper.selectedDatapath,
    }),
    showEmptyPayloadAlert() {
      return (
        (this.copyOfNode === null || this.copyOfNode === "") &&
        !this.payloadError
      );
    },
  },
  mounted() {
    this.$root.$on("selected", this.itemSelected);

    if(this.singleLine) {
      if(this.qitem) {
        this.node = JSON.stringify(this.qitem);
        this.copyOfNode = JSON.parse(this.node);
      } else {
        this.node = this.viewSource
        this.copyOfNode = JSON.parse(this.viewSource)
      }
    } else {
      if(this.qitem) {
        this.node = JSON.stringify(this.qitem);
        this.copyOfNode = JSON.parse(this.node);
      } else {
        this.node = this.viewSource
        this.copyOfNode = JSON.parse(this.viewSource)
      }
    }
  },
  methods: {
    isSelected(label) {
      return this.selectedLabels.has(label);
    },
    closeDataMapper() {
      this.$root.$emit("closeDataMapper", this.node);

      if(this.singleLine) {
        this.$root.$emit('update-editor-form'); //moved from FormLine
      }
    },
    findLabels: debounce(function () {
      this.searchMatches = [];
      if (this.searchString.length > 0) {
        const a = Array.prototype.slice.apply(
          document.querySelectorAll(`[id*="m-value|${this.searchString}" i]`)
        );
        for (let i = 0; i < a.length; ++i) {
          const id = a[i].id;
          const readableLabel = id
            .replace("m-value|", "")
            .substring(0, id.length - 45);
          this.searchMatches.push({
            readableLabel,
            id,
          });
        }
      }
    }, 300),
    scrollTo(id) {
      const element = document.getElementById(id);
      element.classList.add("selected");
      setTimeout(() => {
        element.classList.remove("selected");
      }, 5000);
      element.scrollIntoView(true);
    },
    findAllLabelMatches(id) {
      const label = id.label;
      if(this.selectedLabels.has(label)) {
        this.selectedLabels.delete(label);
        this.$forceUpdate();

        const a = Array.prototype.slice.apply(
          document.querySelectorAll(`[id*="m-value-wrapper|${label}"]`)
        );

        for (let i = 0; i < a.length; ++i) {
          const id = a[i].id;
          const element = document.getElementById(id);
          element.classList.remove("selected");
        }
        return;
      }

      const a = Array.prototype.slice.apply(
        document.querySelectorAll(`[id*="m-value-wrapper|${id.label}"]`)
      );
      if(a.length === 0) {
        this.snackBarText = "No matches found";
        this.snackBarStatus = 'error';
        this.snackbar = true;
        return;
      }
      for (let i = 0; i < a.length; ++i) {
        const id = a[i].id;
        const label = a[i].dataset.label;
        const element = document.getElementById(id);
        this.selectedLabels.add(label)
        element.classList.add("selected");
      }
      this.$forceUpdate();
    },
    removeSelectedLabelClass(id) {
      const a = Array.prototype.slice.apply(
        document.querySelectorAll(`[id*="m-value-wrapper|${id.label}"]`)
      );
      if(a.length === 0) {
        this.snackBarText = "No matches found";
        this.snackBarStatus = 'error';
        this.snackbar = true;
        return
      }
      for (let i = 0; i < a.length; ++i) {
        const id = a[i].id;
        const element = document.getElementById(id);
        this.selectedLabels.push(id);
        element.classList.add("selected");
      }
    },
    removeLabel(id) {
      // dispatch action to remove from store
      this.snackBarText = `Removing... ${id}`;
      this.snackBarStatus = 'success';
      this.snackbar = true;
      this.$store.dispatch("removedDataMapperLabel", id);
    },
    add() {
      if (this.selected === "") {
        this.snackBarText = "Please make a selection first";
        this.snackBarStatus = 'error';
        this.snackbar = true;
        return;
      } else if (this.label === "") {
        this.snackBarText = "Please set a label first";
        this.snackBarStatus = 'error';
        this.snackbar = true;
        return;
      }

      this.$store.dispatch("addTag", {
        label: this.label,
        path: this.selected,
      });
      this.all[this.label] = { path: this.path, selection: this.selection };
      this.label = "";
    },
    formatChange(format) {
      this.parser(format);
    },
    displayErrorMessage(message) {
      this.snackBarText = message;
      this.snackBarStatus = 'error';
      this.snackbar = true;
    },
    parser: debounce(function () {
      this.payloadError = false;
      let self = this;
      try {
        if (self.node.length) {
          self.copyOfNode = JSON.parse(self.node);
        } else {
          self.copyOfNode = "";
        }
      } catch (e) {
        this.payloadError = true;
        self.copyOfNode = "";
      }
    }, 250),
    selectSelectedLabel(sel) {
      let self = this;
      self.path = sel.path;
      self.label = sel.label;
      self.selected = sel.path;
      request.post("/editor/datamapper/select", sel).then((res) => {
        self.sel = res.data;
      });
      this.$store.dispatch("updateDataPath", sel);
    },
    itemSelected(sel) {

      if(this.singleLine) {
        const regex = /\/a\d*\//g;
        sel.selected = sel.selected.replace(regex, '/a/');
      }

      let self = this;
      sel.payload = self.copyOfNode;
      self.path = sel.path;
      self.label = sel.label;
      self.selected = sel.selected;
      self.selection = sel.selection;
      request.post("/editor/datamapper/select", sel).then((res) => {
        self.sel = res.data;
      });

      this.$store.dispatch("updateDataPath", sel);
    },
    isObject: (o) => {
      return typeof o == "object";
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .selected {
  border: 1px dashed black !important;
  background-color: #fdd !important;
  color: black !important;
}

.search-results {
  height: 40px;
  overflow-y: auto;
}

::v-deep textarea {
  height: calc(100vh - 200px);
  overflow-y: auto;
}

::v-deep .datamapper {
  overflow-y: auto;
  height: calc(100vh - 190px);
  cursor: pointer;

  &-small {
    height: calc(100vh - 272px);
  }

  &-error {
    height: calc(100vh - 265px);
  }

  &__header {
    display: flex;
    justify-content: space-between;
  }

  &__close {
    cursor: pointer;
  }
}
.small {
  width: 300px;
}

textarea {
  height: calc(100vh - 24px) !important;
}

.grey {
  background-color: #ddd;
}
</style>


